import { memo, useRef, useState, useEffect, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

import { prefersReducedMotion } from "@/animations";

import styles from "./styles.module.css";

const COUNT = 720;

function FloatingParticles() {
  const points = useRef<THREE.Points>(null);
  const velocities = useMemo(() => {
    const v = new Float32Array(COUNT * 3);
    for (let i = 0; i < COUNT; i++) {
      v[i * 3] = (Math.random() - 0.5) * 0.008;
      v[i * 3 + 1] = (Math.random() - 0.5) * 0.006;
      v[i * 3 + 2] = (Math.random() - 0.5) * 0.005;
    }
    return v;
  }, []);

  const positions = useMemo(() => {
    const arr = new Float32Array(COUNT * 3);
    for (let i = 0; i < COUNT; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 18;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 10;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 8;
    }
    return arr;
  }, []);

  useFrame(({ clock }) => {
    if (!points.current) return;
    const t = clock.getElapsedTime();
    const pos = points.current.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < COUNT; i++) {
      let x = pos.getX(i) + velocities[i * 3];
      let y = pos.getY(i) + velocities[i * 3 + 1] + Math.sin(t * 0.35 + i * 0.05) * 0.0015;
      let z = pos.getZ(i) + velocities[i * 3 + 2];

      if (x > 9 || x < -9) velocities[i * 3] *= -1;
      if (y > 5 || y < -5) velocities[i * 3 + 1] *= -1;
      if (z > 4 || z < -4) velocities[i * 3 + 2] *= -1;

      pos.setXYZ(i, x, y, z);
    }
    pos.needsUpdate = true;
    points.current.rotation.y = t * 0.025;
  });

  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.045}
        color="#8ad9d0"
        transparent
        opacity={0.55}
        sizeAttenuation
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

function AccentParticles() {
  const points = useRef<THREE.Points>(null);
  const positions = useMemo(() => {
    const n = 120;
    const arr = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 16;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 9;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 6;
    }
    return arr;
  }, []);

  useFrame(({ clock }) => {
    if (!points.current) return;
    const t = clock.getElapsedTime();
    points.current.rotation.y = -t * 0.04;
    points.current.rotation.x = Math.sin(t * 0.2) * 0.06;
  });

  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.09}
        color="#46c6dd"
        transparent
        opacity={0.35}
        sizeAttenuation
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

function Scene() {
  return (
    <>
      <FloatingParticles />
      <AccentParticles />
    </>
  );
}

function ParticleCanvas() {
  return (
    <Canvas
      className={styles.canvas}
      camera={{ position: [0, 0, 6.5], fov: 50 }}
      dpr={[1, 1.5]}
      gl={{ antialias: false, alpha: true, powerPreference: "high-performance" }}
    >
      <Scene />
    </Canvas>
  );
}

export const HeroParticleField = memo(function HeroParticleField() {
  const [ready, setReady] = useState(false);
  const reduced = prefersReducedMotion();

  useEffect(() => {
    setReady(true);
  }, []);

  if (reduced || !ready) return null;

  return (
    <div className={styles.wrap} aria-hidden="true">
      <ParticleCanvas />
    </div>
  );
});

export default HeroParticleField;
